/* ==================================================================================================================
 * Bluebell: src/app/state/file-exchange/file-exchange.reducer.ts
 * Last Edited: 21/02/2025
 * Copyright 2025 Bramble CFD LTD
 * The contents of this file are NOT for redistribution
 * See AUTHORS for list of developers on project
 * ================================================================================================================== */
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { TreeNode } from 'primeng/api';
import { createReducer, on } from '@ngrx/store';
import { FileExchangeActions } from '@state/file-exchange/file-exchange.actions';

export interface State {
    expandedNodes: string[];
    fileXData: TreeNode[];
    loading: boolean;
    loaded: boolean;
    space: { bytesAllowed: number, bytesUsed: number };
}

function mapNodeExpansion(node: TreeNode, expandedNodes: string[]): TreeNode {
    return Object.assign({}, node, {
        expanded: expandedNodes.indexOf(node.data) > -1,
        children: node.children ? [...node.children.map(child => mapNodeExpansion(child, expandedNodes))] : null
    });
}

export const initialState: State = {
  expandedNodes: [],
  fileXData: [],
  loading: false,
  loaded: false,
  space: { bytesAllowed: 0, bytesUsed: 0 },
};

export const reducer = createReducer(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return initialState;
  }),

  on(FileExchangeActions.expandedNodeAdd, (state, { uuid }) => {
    return {
      ...state,
      expandedNodes: [...state.expandedNodes, uuid],
    };
  }),

  on(FileExchangeActions.expandedNodeRemove, (state, { uuid }) => {
    return {
      ...state,
      expandedNodes: [...state.expandedNodes.filter(data => data !== uuid)]
    };
  }),

  on(FileExchangeActions.list, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),

  on(FileExchangeActions.listFailure, (state) => {
    return {
      ...state,
      loading: false,
    };
  }),

  on(FileExchangeActions.listSuccess, (state, { data }) => {
    return {
      ...state,
      fileXData: [...data.data.map(node => mapNodeExpansion(node, state.expandedNodes))],
      loading: false,
      space: {bytesAllowed: data.allowed_bytes, bytesUsed: data.used_bytes}
    };
  }),

  on(FileExchangeActions.makeDirSuccess, (state, { data }) => {
    return {
      ...state,
      fileXData: [...data.data.map(node => mapNodeExpansion(node, state.expandedNodes))],
      loading: false,
      space: {bytesAllowed: data.allowed_bytes, bytesUsed: data.used_bytes}
    };
  }),

  on(FileExchangeActions.moveSuccess, (state, { data }) => {
    return {
      ...state,
      fileXData: [...data.data.map(node => mapNodeExpansion(node, state.expandedNodes))],
      loading: false,
      space: {bytesAllowed: data.allowed_bytes, bytesUsed: data.used_bytes}
    };
  }),

  on(FileExchangeActions.removeSuccess, (state, { data }) => {
    return {
      ...state,
      fileXData: [...data.data.map(node => mapNodeExpansion(node, state.expandedNodes))],
      loading: false,
      space: {bytesAllowed: data.allowed_bytes, bytesUsed: data.used_bytes}
    };
  }),

);

export const featureKey = 'fileExchange';
export const getFiles = (state: State) => state.fileXData;
export const getLoading = (state: State) => state.loading;
export const getLoaded = (state: State) => state.loaded;
export const getSpace = (state: State) => state.space;
